import { ButtonBase, Stack, Typography } from '@mui/material';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { useIsElementHovered } from '@/hooks/useIsElementHovered';
import { COLORS } from '@/styles/tokens/colors';

interface ChatConversationListItemProps {
  onClick: () => void;
  /** Called when the user clicks the delete icon for this conversation. */
  onDelete?: () => void;
  /** Pre-formatted relative time label, e.g. "2 hours ago". */
  timestamp: string;
  title: string;
}

/**
 * One clickable row in a chat conversation list: a single-line title with a
 * relative timestamp on the right.
 */
export function ChatConversationListItem({
  onClick,
  onDelete,
  timestamp,
  title,
}: ChatConversationListItemProps) {
  const [isHovered, hoverProps] = useIsElementHovered();

  return (
    <Stack
      data-testid="chat-conversation-list-item"
      direction="row"
      sx={{
        '&:hover': { backgroundColor: COLORS.GRAY[100] },
        alignItems: 'center',
        height: 40,
        position: 'relative',
        width: '100%',
      }}
      {...hoverProps}
    >
      <ButtonBase
        onClick={onClick}
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          height: '100%',
          justifyContent: 'space-between',
          px: 2,
          textAlign: 'left',
          width: '100%',
        }}
      >
        <Typography
          noWrap
          variant="body-xs"
          sx={{ color: COLORS.TEXT.PRIMARY, flexGrow: 1, minWidth: 0 }}
        >
          {title}
        </Typography>
        <Typography
          noWrap
          variant="body-xs"
          sx={{ display: isHovered ? 'none' : undefined, flexShrink: 0 }}
        >
          {timestamp}
        </Typography>
      </ButtonBase>
      {onDelete && (
        <IconButton
          ariaLabel="Delete conversation"
          icon={Trash01Icon}
          onClick={onDelete}
          size="xs"
          variant="transparent"
          sx={{
            display: isHovered ? 'inline-flex' : 'none',
            position: 'absolute',
            right: 1,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}
    </Stack>
  );
}
